/**
 * useMapRepair — React Query hook dla Map Repair Engine v1.
 *
 * Exports:
 *  - useRepairReport(mapId) — query dla thematic_maps.repair_report
 *  - useRollbackPreview(mapId) — query dla agregacji map_repair_log (preview count)
 *  - useMapRepair(mapId) — mutations: repair (dry-run + apply) + rollback
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { repairMap } from "@/services/repair/mapRepairEngine";
import {
  rollbackRepair,
  previewRollback,
} from "@/services/repair/repairRollback";
import type {
  RepairMapOptions,
  RepairReport,
  RepairStatus,
} from "@/types/mapRepair";

type LogCb = NonNullable<RepairMapOptions["onLog"]>;

interface MapRepairRow {
  repair_report: RepairReport | null;
  last_repaired_at: string | null;
  repair_status: RepairStatus | null;
  repair_rollback_available_until: string | null;
}

async function fetchRepairRow(mapId: string): Promise<MapRepairRow | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("thematic_maps")
    .select(
      "repair_report, last_repaired_at, repair_status, repair_rollback_available_until",
    )
    .eq("id", mapId)
    .maybeSingle();
  if (error) throw error;
  return (data as MapRepairRow) || null;
}

export function useRepairReport(mapId: string | null | undefined) {
  return useQuery({
    queryKey: ["repair-report", mapId],
    queryFn: () => (mapId ? fetchRepairRow(mapId) : null),
    enabled: !!mapId,
  });
}

export function useRollbackPreview(mapId: string | null | undefined) {
  return useQuery({
    queryKey: ["rollback-preview", mapId],
    queryFn: () => (mapId ? previewRollback(mapId) : null),
    enabled: !!mapId,
  });
}

export function useMapRepair(mapId: string | null | undefined) {
  const qc = useQueryClient();

  const invalidate = () => {
    if (!mapId) return;
    qc.invalidateQueries({ queryKey: ["repair-report", mapId] });
    qc.invalidateQueries({ queryKey: ["rollback-preview", mapId] });
    qc.invalidateQueries({ queryKey: ["audit-report", mapId] });
    qc.invalidateQueries({ queryKey: ["thematic-maps"] });
    qc.invalidateQueries({ queryKey: ["thematic-nodes", mapId] });
    qc.invalidateQueries({ queryKey: ["thematic-seeds-count", mapId] });
  };

  const repair = useMutation({
    mutationFn: async (opts: {
      dryRun: boolean;
      acceptedR3?: string[];
      acceptedR6?: string[];
      onLog?: LogCb;
    }) => {
      if (!mapId) throw new Error("mapId required");
      return repairMap(mapId, opts);
    },
    onSuccess: invalidate,
  });

  const rollback = useMutation({
    mutationFn: async (opts: { onLog?: LogCb } = {}) => {
      if (!mapId) throw new Error("mapId required");
      return rollbackRepair(mapId, opts);
    },
    onSuccess: invalidate,
  });

  const repairRow = useRepairReport(mapId);
  const rollbackPreview = useRollbackPreview(mapId);

  return {
    repair,
    rollback,
    repairRow,
    rollbackPreview,
  };
}
